import React, { useEffect, useRef, useState } from "react";
import { Calendar, CalendarChangeParams, CalendarProps } from "primereact/calendar";
import useDebounce from "hooks/useDebounce";
import { ChronoUnitLegacy } from "utils/time-utils";

import { JodaTimeUtils } from "utils/joda-time-utils";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";

type CalendarWrapperProps = {
  value: Date | undefined;
  onChange(date: Date): void;
  dateFrequency?: ChronoUnitLegacy;
  debounceTimeInMs?: number;
} & Omit<CalendarProps, "value" | "onChange">;

const CalendarWrapper = ({
  value,
  onChange,
  dateFrequency,
  debounceTimeInMs,
  ...props
}: CalendarWrapperProps) => {
  const valueRef = useRef<Date | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const debouncedValue = useDebounce(date, { delay: debounceTimeInMs });

  useEffect(() => {
    const handleEnterPressed = (event: KeyboardEvent) =>
      (event.code === "Enter" || event.code === "NumpadEnter") && replaceDateValue();

    window.addEventListener("keydown", handleEnterPressed);

    return () => window.removeEventListener("keydown", handleEnterPressed);
  }, []);

  useEffect(() => {
    debouncedValue && onChange(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    valueRef.current && (valueRef.current = null);
  }, [value]);

  const replaceDateValue = () => {
    if (valueRef.current != null && value !== valueRef.current) {
      onChange(valueRef.current);
    }
  };

  const onCalendarChange = (e: CalendarChangeParams) => {
    if (!e.value) return;

    const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
      dateFrequency || ChronoUnitLegacy.MINUTES
    );
    const pickedDate = JodaTimeUtils.truncateTo(e.value as Date, tempFrequency);

    if (e.originalEvent.type === "input") {
      valueRef.current = pickedDate;
    } else if (e.originalEvent.type === "click") {
      onChange(pickedDate);
    } else if (e.originalEvent.type === "mousedown") {
      setDate(pickedDate);
    }
  };

  const calendarFooterTemplate = () => {
    return (
      <div className="flex flex-row justify-content-between">
        <Button
          type="button"
          label={T("action.truncate-hours")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
              ChronoUnitLegacy.DAYS
            );
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, tempFrequency);
            onChange(truncatedDate);
          }}
        />
        <Button
          type="button"
          label="Truncate minutes"
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
              ChronoUnitLegacy.HOURS
            );
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, tempFrequency);
            onChange(truncatedDate);
          }}
        />
      </div>
    );
  };

  return (
    <Calendar
      value={value}
      onChange={onCalendarChange}
      footerTemplate={props.showTime === false ? undefined : calendarFooterTemplate}
      showIcon
      iconPos="left"
      dateFormat="yy-mm-dd"
      showTime={props.showTime ?? true}
      onBlur={() => replaceDateValue()}
      {...props}
    />
  );
};

export default CalendarWrapper;
